import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Search } from 'lucide-react';
import { useEmailVerification, EmailVerificationModal } from '../components/EmailVerification';

// Static brands data - no database
// All brands now have consistent structure with background colors for logo display
const brands = [
  { 
    id: 1, 
    name: "Waphyto", 
    category: "Total Care", 
    image: "/images/brands/seichajo.jpg", 
    description: "Plant-based holistic beauty from Higashimikawa, Japan. Pioneering phytotherapy skincare.",
    isLogo: true
  },
  { 
    id: 2, 
    name: "Seichajo Yamashina", 
    category: "Matcha", 
    image: "/images/brands/waphyto.png", 
    description: "Premium artisan matcha from Kyoto's heritage tea masters since 1860.",
    isLogo: true
  },
  { 
    id: 3, 
    name: "Tamanohada", 
    category: "Body", 
    image: "/images/brands/tamanohada.png", 
    description: "Aromatic body care inspired by Japanese minimalism and natural ingredients.",
    isLogo: true
  },
  { 
    id: 4, 
    name: "Sophistance", 
    category: "Skincare", 
    image: "/images/brands/sophistance.png", 
    description: "Advanced skincare technology meets traditional Japanese ingredients.",
    isLogo: true
  },
  { 
    id: 5, 
    name: "BelAge", 
    category: "Supplement", 
    image: "/images/brands/belage.png", 
    description: "Premium health supplements for ageless vitality and inner wellness.",
    isLogo: true
  },
  { 
    id: 6, 
    name: "Grand Fond Blanc", 
    category: "Lifestyle", 
    image: "/images/brands/grandfond.jpg", 
    description: "Luxury lifestyle accessories with Japanese craftsmanship and elegance.",
    isLogo: true
  },
  { 
    id: 7, 
    name: "Fairydrops", 
    category: "Makeup", 
    image: "/images/brands/fairydrops.png", 
    description: "Iconic Japanese mascaras designed to enhance natural eye expression.",
    isLogo: true
  },
  { 
    id: 8, 
    name: "Lacasta", 
    category: "Hair Care", 
    image: "/images/brands/lacasta.jpg", 
    description: "Where Japanese botanicals meet aromatherapy-driven hair and scalp care.",
    isLogo: true
  },
  { 
    id: 9, 
    name: "Mesoins", 
    category: "Self Care", 
    image: "/images/brands/mesoins.png", 
    description: "Where Japanese simplicity meets effective, skin-first solutions.",
    isLogo: true
  },
  { 
    id: 10, 
    name: "Arpa", 
    category: "Fragrance", 
    image: "/images/brands/arpa.png", 
    description: "Multisensory fragrance atelier blending art, scent, memory, and sensory exploration.",
    isLogo: true
  },
  { 
    id: 11, 
    name: "Intime Organics", 
    category: "Intimate Care", 
    image: "/images/brands/intime.png", 
    description: "Japanese organic feminine care designed for comfort, balance, and daily wellbeing.",
    isLogo: true
  },
  { 
    id: 12, 
    name: "Loqzin", 
    category: "Skincare", 
    image: "/images/brands/loqzin.jpg", 
    description: "Microbiome-focused Japanese skincare enriched with loquat fermentation science.",
    isLogo: true
  },
];

const BrandsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredBrand, setHoveredBrand] = useState(null);
  
  // Email verification hook
  const emailVerification = useEmailVerification();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = ['all', ...new Set(brands.map((b) => b.category))];

  const filteredBrands =
    selectedCategory === 'all'
      ? brands
      : brands.filter((b) => b.category === selectedCategory);

  return (
    <div className="min-h-screen bg-onyx">
      <Helmet>
        <title>Our Brands - Luxe Me Japan | Premium Japanese Beauty & Lifestyle Portfolio</title>
        <meta name="description" content="Explore our curated collection of Japanese beauty, skincare, wellness, and lifestyle brands. From Waphyto to Intime Organics, discover authentic Japanese excellence." />
      </Helmet>
      
      {/* Hero Section */}
      <header className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-gold/10 via-onyx to-onyx"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-3 text-gold tracking-[0.4em] text-[10px] uppercase mb-8 opacity-0 animate-fade-up">
              <span className="w-8 h-px bg-gold/50"></span> Our Portfolio
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl mb-8 opacity-0 animate-fade-up stagger-1 leading-tight">
              Featured <br />
              <span className="italic text-gradient">Curated Brands</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-light tracking-wide text-platinum/60 max-w-2xl opacity-0 animate-fade-up stagger-2">
              A curated selection of excellence across beauty, skincare, and lifestyle from Japan's finest artisans.
            </p>
          </div>
        </div>
      </header>

      {/* Filter Section */}
      <section className="py-6 sm:py-8 px-4 sm:px-6 lg:px-12 bg-ebony border-y border-white/5 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Search className="w-4 h-4 text-gold" />
            <span className="text-xs uppercase tracking-widest text-platinum/50">
              Filter by Category:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 sm:px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all duration-300 ${
                  selectedCategory === cat
                    ? 'bg-gold text-onyx'
                    : 'border border-white/10 text-platinum/60 hover:border-gold hover:text-gold'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Grid - UNIFORM DESIGN */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-onyx">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredBrands.map((brand) => (
              <div
                key={brand.id}
                className="group relative overflow-hidden rounded-2xl aspect-[3/4] cursor-pointer"
                onMouseEnter={() => setHoveredBrand(brand.id)}
                onMouseLeave={() => setHoveredBrand(null)}
              >
                {/* Background - Logo brands get styled background, others get image */}
                {brand.isLogo ? (
                  <div className={`absolute inset-0 bg-gradient-to-br from-gray-100 via-white to-gray-50 flex items-center justify-center p-8 sm:p-12 transition-all duration-700 ${
                    hoveredBrand === brand.id ? '' : 'grayscale'
                  }`}>
                    <img
                      src={brand.image}
                      alt={brand.name}
                      className={`max-w-[70%] max-h-[50%] object-contain transition-all duration-700 ${
                        hoveredBrand === brand.id ? 'scale-110' : 'scale-100'
                      }`}
                    />
                  </div>
                ) : (
                  <img
                    src={brand.image}
                    className={`w-full h-full object-cover transition-all duration-700 ${
                      hoveredBrand === brand.id ? 'scale-110 grayscale-0' : 'grayscale'
                    }`}
                    alt={brand.name}
                  />
                )}
                
                {/* Gradient Overlay - Same for all */}
                <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/40 to-transparent opacity-90"></div>

                {/* Content - SAME for all brands */}
                <div className="absolute bottom-0 left-0 p-6 sm:p-8 w-full transform translate-y-2 group-hover:translate-y-0 transition-transform duration-700">
                  <span className="text-gold uppercase tracking-[0.3em] text-[10px] block mb-2">
                    {brand.category}
                  </span>
                  <h4 className="font-serif text-2xl sm:text-3xl mb-3 text-platinum group-hover:text-white transition-colors">
                    {brand.name}
                  </h4>
                  <p className="text-platinum/60 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500 line-clamp-2">
                    {brand.description}
                  </p>
                  <div className="h-px w-0 group-hover:w-full bg-gold/50 transition-all duration-1000 mt-4"></div>
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-12 bg-ebony">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-6">
            Let's Create <br />
            <span className="text-gold italic">Together</span>
          </h2>
          <p className="text-platinum/60 leading-relaxed text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            Connect with us to explore how we can make your vision a reality. Join
            us in shaping the future of Japanese beauty and lifestyle.
          </p>
          <button
            onClick={emailVerification.handleEmailClick}
            className="inline-flex items-center gap-3 bg-gold text-onyx px-8 py-4 rounded-full font-medium tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 shine-effect"
          >
            Get in Touch <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
      
      {/* Email Verification Modal */}
      <EmailVerificationModal 
        showVerification={emailVerification.showVerification}
        selectedAnswer={emailVerification.selectedAnswer}
        verificationError={emailVerification.verificationError}
        setSelectedAnswer={emailVerification.setSelectedAnswer}
        setVerificationError={emailVerification.setVerificationError}
        handleVerify={emailVerification.handleVerify}
        closeModal={emailVerification.closeModal}
      />
    </div>
  );
};

export default BrandsPage;
